import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { getCustomerDataPreview } from '../redux/adminSlice';

// Whitelist mirrors adminDashboardF.js's DATA_PREVIEW_COLLECTIONS exactly -
// this copy is display-only (dropdown labels), the server re-validates
// `collection` against its own whitelist regardless, so a stale/mismatched
// copy here is a UX gap, never a security gap. Keep in sync by hand.
const DATA_PREVIEW_COLLECTIONS = [
  { key: 'employees', label: 'Employees' },
  { key: 'jobsites', label: 'Jobsites' },
  { key: 'gcWorkers', label: 'GC Workers' },
  { key: 'gcSubCompanies', label: 'GC Sub-Companies' },
  { key: 'gcTags', label: 'GC Tags' },
];

// Renders one (already-redacted, server-side) field value for display. The
// server's formatPreviewValue already reduced every value to a JSON-safe
// primitive or a one-level-deep plain object (see adminDashboardF.js) - this
// only has to turn THAT into readable text, no further redaction happens
// here.
const renderFieldValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

// Per-customer read-only data browser ("Data" button in the Customers
// table). Lets the super-admin pick one whitelisted collection and preview
// its documents - id + a compact, server-redacted field list - without ever
// writing anything. Load is a deliberate manual trigger (no auto-fetch on
// dropdown change) so browsing the dropdown never fires a query by itself.
const CustomerDataModal = ({ customer, onClose }) => {
  const dispatch = useDispatch();
  const [collection, setCollection] = useState(DATA_PREVIEW_COLLECTIONS[0].key);
  const [docs, setDocs] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);

  if (!customer) {
    return null;
  }

  const handleCollectionChange = (e) => {
    setCollection(e.target.value);
    // Clear stale results so switching collections never looks like a
    // still-loaded (but wrong) result set - the admin must click Load again.
    setDocs(null);
    setError(null);
  };

  const handleLoad = async () => {
    if (loadingRef.current) {
      return; // sync guard: a double-click can never double-submit
    }
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const result = await dispatch(
        getCustomerDataPreview({ customerId: customer.id, collection })
      ).unwrap();
      setDocs(result.docs || []);
    } catch (err) {
      setDocs(null);
      setError(typeof err === 'string' ? err : 'Failed to load data preview. Please try again.');
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-[rgba(150,165,180,.25)] bg-white shadow-[0_12px_40px_rgba(29,43,51,.35)]">
        <div className="flex flex-none items-center justify-between gap-4 rounded-t-xl bg-[linear-gradient(120deg,#4a5c70,#374a5e_55%,#2a3a4c)] px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-white">Data</h3>
            <p className="truncate text-sm text-[rgba(215,226,236,.75)]">{customer.name || customer.email}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-none rounded-lg border border-white/25 p-1.5 text-white transition hover:bg-white/10"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="mb-4 rounded-lg bg-[#f4f7fa] px-3 py-2 text-xs font-semibold text-[#64798a]">
            Read-only view. Nothing on this screen can edit or delete tenant data.
            Sensitive-looking fields (tokens, secrets, passwords, signatures) are hidden.
          </p>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <select
              value={collection}
              onChange={handleCollectionChange}
              disabled={isLoading}
              className="rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-sm text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {DATA_PREVIEW_COLLECTIONS.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleLoad}
              disabled={isLoading}
              className="rounded-lg bg-[#2f66b8] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#285aa5] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : 'Load'}
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-lg bg-[#d96a5b] p-3 text-sm text-white">{error}</div>
          )}

          {!error && docs === null && (
            <p className="text-sm text-[#64798a]">Choose a collection and click Load.</p>
          )}

          {!error && docs !== null && docs.length === 0 && (
            <p className="text-sm text-[#64798a]">No documents found.</p>
          )}

          {!error && docs !== null && docs.length > 0 && (
            <div className="space-y-3">
              {docs.map((doc) => (
                <div
                  key={doc.id}
                  className="rounded-lg border border-[rgba(150,165,180,.25)] bg-white p-3 text-sm"
                >
                  <p className="mb-1 font-mono text-xs font-bold text-[#2f66b8]">{doc.id}</p>
                  <div className="space-y-0.5">
                    {Object.keys(doc.fields || {}).length === 0 ? (
                      <p className="text-xs text-[#64798a]">(no fields)</p>
                    ) : (
                      Object.entries(doc.fields).map(([key, value]) => (
                        <div key={key} className="flex gap-1 text-xs text-[#1d2b33]">
                          <span className="flex-none font-semibold text-[#64798a]">{key}:</span>
                          <span className="break-all">{renderFieldValue(value)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-none items-center justify-end gap-2 border-t border-[rgba(150,165,180,.25)] bg-white px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[rgba(150,165,180,.4)] bg-white px-4 py-2 text-sm font-semibold text-[#1d2b33] transition hover:bg-[#f4f7fa]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerDataModal;
